import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import EditTopicName from 'components/EditTopicName/EditTopicName';
import Loader from 'components/Loader/Loader';
import TitleTableWrapper from 'components/TitleTableWrapper/TitleTableWrapper';
import { BUTTON_VARIANT, DELETE_MODAL_TITLES } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import Tabs from '../../../components/Tabs/Tabs';
import SubtopicRow from './components/SubtopicRow';
import TopicRow from './components/TopicRow';

import type IStandards from './interfaces/IStandards';

import styles from './scss/Standards.module.scss';

function StandardsView({
  loading, addTopic, standards, activeTab, onClickTab,
  onLoadMore, hasMore, topics, openNewTopic, onCreateNewTopic,
  onCancel, onDeleteItem, isDeleteModalOpen,
  onDeleteConfirm, onEditTopicName, openEditField, selectedItem,
  onChangeText, text, onSaveTopic, addSubTopic, openEditSubTopicField,
  onCreateNewSubtopic, onEditSubtopicName, onUpdateSubtopic, isSubtopic,
  openKeywordsField, onOpenFieldKeyWords, onSaveKeyWords, onDeleteKeyword,
}: Readonly<IStandards>) {
  const { t } = useTranslation();

  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;

    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    const clientHeight = document.documentElement.clientHeight || window.innerHeight;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className={styles.root}>
      <div className={styles.headerWrapper}>
        <TitleTableWrapper
          title={t('standards.standards')}
          buttonTitle={t('standards.addTopic')}
          onButtonClick={addTopic}
          buttonClassName={styles.button}
          classNameTitle={styles.header}
          showButton
        />
      </div>

      <div className={styles.contentWrapper}>
        <Tabs
          data={standards}
          activeTab={activeTab}
          onClickTab={onClickTab}
        />
        <div className={styles.content}>
          {loading ? <Loader isPageLoading={loading} isFullLoader={false} /> : null}
          {!loading ? (
            <div className={styles.tableTopics}>
              <div className={styles.tableHeader}>
                <span>{t('standards.topic')}</span>
                <span>{t('standards.subTopic')}</span>
                <span>{t('standards.keySearch')}</span>
                <span />
              </div>
              <div className={styles.tableBody}>
                {topics.map((topic) => (
                  <div className={styles.contentItem} key={[topic.id, topic.title].join('-')}>
                    <TopicRow
                      topic={topic}
                      openEditField={openEditField}
                      selectedItem={selectedItem}
                      text={text}
                      onChangeText={onChangeText}
                      onSaveTopic={onSaveTopic}
                      onCancel={onCancel}
                      openKeywordsField={openKeywordsField}
                      onOpenFieldKeyWords={onOpenFieldKeyWords}
                      onSaveKeyWords={onSaveKeyWords}
                      onDeleteKeyword={onDeleteKeyword}
                      onEditTopicName={onEditTopicName}
                      onDeleteItem={onDeleteItem}
                    />
                    <div className={styles.subtopicContainer}>
                      <div className={styles.emptyBox} />
                      <div className={styles.subtopicBox}>
                        <div className={styles.subTopicAction}>
                          {openEditSubTopicField && selectedItem === topic.id
                            ? (
                              <div className={styles.editSubTopicContainer}>
                                <EditTopicName
                                  text={text}
                                  placeholder={t('standards.typeHere')}
                                  onChangeText={onChangeText}
                                  onSave={onCreateNewSubtopic}
                                  onCancel={onCancel}
                                  saveTitle={t('common.save')}
                                  cancelTitle={t('common.cancel')}
                                />
                              </div>
                            )
                            : (
                              <Button
                                title={t('standards.addSubTopic')}
                                variant={BUTTON_VARIANT.ghost}
                                onClick={() => addSubTopic(topic.id || -1)}
                                className={styles.buttonAddSubTopic}
                              />
                            )}
                        </div>
                        {topic?.children && topic?.children?.length > 0 ? topic?.children.map((subTopic) => (
                          <SubtopicRow
                            key={[subTopic.id, subTopic.title].join('_')}
                            subTopic={subTopic}
                            openEditSubTopicField={openEditSubTopicField}
                            selectedItem={selectedItem}
                            text={text}
                            onChangeText={onChangeText}
                            onUpdateSubtopic={onUpdateSubtopic}
                            onCancel={onCancel}
                            openKeywordsField={openKeywordsField}
                            onOpenFieldKeyWords={onOpenFieldKeyWords}
                            onSaveKeyWords={onSaveKeyWords}
                            onDeleteKeyword={onDeleteKeyword}
                            onEditSubtopicName={onEditSubtopicName}
                            onDeleteItem={onDeleteItem}
                          />
                        )) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <DeactivationModal
        className={styles.modal}
        titleClassName={styles.modalTitle}
        open={openNewTopic}
        title={t('standards.addNewTopic')}
        titleLabel={t('standards.topicName')}
        placeholder={t('standards.topicNamePlaceholder')}
        confirmTitle={t('standards.addNew')}
        onReject={onCancel}
        onSubmit={onCreateNewTopic}
        isLoading={false}
        offValidation
        textFieldType
        disabledConfirmBtn
      />
      <DeleteModal
        open={isDeleteModalOpen}
        onReject={onCancel}
        isLoading={false}
        title={isSubtopic ? DELETE_MODAL_TITLES.DELETE_STANDARD_SUBTOPIC : DELETE_MODAL_TITLES.DELETE_STANDARD_TOPIC}
        hint={isSubtopic ? DELETE_MODAL_TITLES.DELETE_STANDARD_SUBTOPIC_HINT : DELETE_MODAL_TITLES.DELETE_TOPIC_HINT}
        onConfirm={onDeleteConfirm}
      />
    </div>
  );
}

export default StandardsView;
